import React, { useCallback, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  Position,
  Handle,
  NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { GraphNode, NodeStatus, Workflow } from '../../../types/workflow';

const STATUS_COLOR: Record<NodeStatus, { border: string; bg: string; chip: string; label: string }> = {
  idle:    { border: '#CBD5E1', bg: '#FFFFFF', chip: '#F5F7FA', label: 'Idle' },
  running: { border: '#0ea5e9', bg: '#E0F2FE', chip: '#0ea5e9', label: 'Running' },
  success: { border: '#10b981', bg: '#ECFDF5', chip: '#10b981', label: 'Success' },
  failed:  { border: '#ef4444', bg: '#FEF2F2', chip: '#ef4444', label: 'Failed' },
  retried: { border: '#f59e0b', bg: '#FFFBEB', chip: '#f59e0b', label: 'Retried' },
};

function WorkflowNodeCard({ data }: NodeProps) {
  const s = STATUS_COLOR[(data.status as NodeStatus) || 'idle'];
  return (
    <div
      className="rounded-lg shadow-sm"
      style={{
        border: `1.5px solid ${s.border}`,
        background: s.bg,
        minWidth: 180,
        padding: '8px 12px',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#94a3b8' }} />
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-xs uppercase tracking-wide text-muted">{data.type}</div>
        <span className="px-1.5 py-px rounded text-[10px] font-medium text-white" style={{ background: s.chip }}>{s.label}</span>
      </div>
      <div className="text-sm font-medium text-ink leading-tight">{data.label}</div>
      <Handle type="source" position={Position.Right} style={{ background: '#94a3b8' }} />
    </div>
  );
}

const nodeTypes = { wfNode: WorkflowNodeCard };

function layoutNodes(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  // Naive horizontal flow layout: even spacing left-to-right, alternating rows for branches.
  const positions = new Map<string, { x: number; y: number }>();
  const COL_W = 230;
  const ROW_H = 110;
  nodes.forEach((n, i) => {
    positions.set(n.id, { x: i * COL_W, y: (i % 2 === 0 ? 0 : 1) * ROW_H });
  });
  // Special: nodes whose id contains "route_no_job" or "archive" go to a second row offset
  nodes.forEach((n, i) => {
    if (n.id === 'route_no_job' || n.id === 'archive') {
      positions.set(n.id, { x: (i - 1) * COL_W, y: ROW_H * 2 });
    }
  });
  return positions;
}

interface Props {
  workflow: Workflow;
  nodeStatusOverride: Record<string, NodeStatus>;
  onNodeSelect: (node: GraphNode | null) => void;
  selectedNodeId: string | null;
}

export function GraphTab({ workflow, nodeStatusOverride, onNodeSelect, selectedNodeId }: Props) {
  const positions = useMemo(() => layoutNodes(workflow.graph.nodes), [workflow.graph.nodes]);

  const flowNodes: Node[] = useMemo(
    () => workflow.graph.nodes.map(n => ({
      id: n.id,
      type: 'wfNode',
      position: positions.get(n.id) || { x: 0, y: 0 },
      data: { label: n.label, type: n.type, status: nodeStatusOverride[n.id] || n.status },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    })),
    [workflow.graph.nodes, positions, nodeStatusOverride]
  );

  const flowEdges: Edge[] = useMemo(
    () => workflow.graph.edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      animated: !!nodeStatusOverride[e.source] && nodeStatusOverride[e.source] === 'running',
      style: { stroke: '#94a3b8' },
      labelStyle: { fontSize: 10, fill: '#595A60' },
    })),
    [workflow.graph.edges, nodeStatusOverride]
  );

  const handleNodeClick = useCallback((_: unknown, n: Node) => {
    const detail = workflow.graph.nodes.find(x => x.id === n.id) || null;
    onNodeSelect(detail);
  }, [workflow.graph.nodes, onNodeSelect]);

  const selected = workflow.graph.nodes.find(n => n.id === selectedNodeId) || null;

  return (
    <div className="flex gap-4" style={{ height: 'calc(100vh - 320px)', minHeight: 480 }}>
      <div className="flex-1 bg-white border border-trim rounded-xl overflow-hidden">
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          fitView
          onNodeClick={handleNodeClick}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} color="#E5E7EB" />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>

      {selected && (
        <aside className="w-80 bg-white border border-trim rounded-xl p-4 overflow-y-auto flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-muted">{selected.type}</div>
            <button onClick={() => onNodeSelect(null)} className="text-xs text-muted hover:text-ink">×</button>
          </div>
          <h4 className="text-sm font-semibold text-ink">{selected.label}</h4>
          <p className="text-xs text-copy mt-1.5 leading-relaxed">{selected.description}</p>
          <div className="text-xs text-muted mt-1">Owner: <span className="text-copy">{selected.owner}</span></div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="bg-wash rounded p-2">
              <div className="text-muted">Avg latency</div>
              <div className="text-ink font-mono">{selected.metrics.avg_latency_ms}ms</div>
            </div>
            <div className="bg-wash rounded p-2">
              <div className="text-muted">p95</div>
              <div className="text-ink font-mono">{selected.metrics.p95_latency_ms}ms</div>
            </div>
            <div className="bg-wash rounded p-2">
              <div className="text-muted">Success</div>
              <div className="text-ink font-mono">{selected.metrics.success_rate}%</div>
            </div>
            <div className="bg-wash rounded p-2">
              <div className="text-muted">Retries 24h</div>
              <div className="text-ink font-mono">{selected.metrics.retries_24h}</div>
            </div>
          </div>

          <div className="mt-3">
            <div className="text-xs font-medium text-ink mb-1">I/O schema</div>
            <pre className="text-xs font-mono bg-wash p-2 rounded text-copy whitespace-pre-wrap">{`in:  ${selected.io_schema.input}\nout: ${selected.io_schema.output}`}</pre>
          </div>

          <div className="mt-3">
            <div className="text-xs font-medium text-ink mb-1">Recent logs</div>
            <ul className="text-xs text-copy space-y-1">
              {selected.recent_logs.map((l, i) => <li key={i} className="font-mono bg-wash p-1.5 rounded">{l}</li>)}
            </ul>
          </div>
        </aside>
      )}
    </div>
  );
}
